import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import Dialog from 'components/DesignSystem/Dialog';
import Button from 'components/DesignSystem/Button';
import BodyRegular from 'components/Typography/BodyRegular';
import useRelatorioAprovacao from 'hooks/Relatorios/Aprovacao/useRelatorioAprovacao';
import { selectTokenUserName } from 'redux/reducers/authReducer';
import { formatarDataBR, labelDaVariante, varianteDoStatus } from 'helper/Relatorios/Aprovacao';

import './index.scss';

export interface PublicarModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Confirmação de publicação (DS `Dialog`). "Publicando como {username}" usa
 * `selectTokenUserName` direto — exceção documentada no PLANO (D3/D5): o hook
 * `useRelatorioAprovacao` não expõe o username do JWT.
 */
export default function PublicarModal({ open, onClose }: PublicarModalProps) {
  const {
    config, status, params, publicar,
  } = useRelatorioAprovacao();
  const username = useSelector(selectTokenUserName);
  const [publicando, setPublicando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const handleConfirmar = useCallback(() => {
    setErro(null);
    setPublicando(true);
    publicar()
      .then(() => {
        setPublicando(false);
        onClose();
      })
      .catch(() => {
        setPublicando(false);
        setErro('Não foi possível publicar o informe. Tente novamente.');
      });
  }, [publicar, onClose]);

  if (!status) return null;

  const dataBR = formatarDataBR(params.data_rodada);
  const cenarioLabel = labelDaVariante(config, varianteDoStatus(config, status));

  return (
    <Dialog open={open} dismiss={onClose} centered className="relatorio-aprovacao-publicar-modal">
      <Dialog.Body>
        <p className="relatorio-aprovacao-publicar-modal__titulo">{`Publicar o informe de ${dataBR}?`}</p>
        <BodyRegular>
          <span className="relatorio-aprovacao-publicar-modal__texto">
            {`Todas as ${status.secoes.length} seções estão aprovadas. Após publicar, o dia `}
            <strong>{dataBR}</strong>
            {` (${config.titulo}, ${config.variante.rotulo} ${cenarioLabel}) fica liberado no calendário dos clientes.`}
          </span>
        </BodyRegular>
        <div className="relatorio-aprovacao-publicar-modal__autoria">
          <BodyRegular>
            {'Publicando como '}
            <strong>{username || 'Admin'}</strong>
            {' · agora'}
          </BodyRegular>
        </div>
        {erro && (
          <BodyRegular>
            <span className="relatorio-aprovacao-publicar-modal__erro">{erro}</span>
          </BodyRegular>
        )}
      </Dialog.Body>
      <Dialog.Footer>
        <Button type="secondary" className="relatorio-aprovacao-publicar-modal__cancelar" onClick={onClose} disabled={publicando} small>
          Cancelar
        </Button>
        <Button className="relatorio-aprovacao-publicar-modal__confirmar" onClick={handleConfirmar} disabled={publicando} small>
          Confirmar publicação
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
